/**
 * Shared video behavior for theme sections.
 * background — muted loop, autoplay when visible (JS-driven)
 * player     — custom play/mute controls, click-to-pause, progress bar
 */
window.ThemeVideo = {
  MOBILE_BREAKPOINT: 575,

  getBehavior(videoEl) {
    const wrapper = videoEl?.closest?.("[data-video-behavior]");
    return wrapper?.dataset?.videoBehavior || "background";
  },

  isBackground(videoEl) {
    return this.getBehavior(videoEl) === "background";
  },

  isPlayer(videoEl) {
    return this.getBehavior(videoEl) === "player";
  },

  isMobileViewport() {
    return document.body.clientWidth <= this.MOBILE_BREAKPOINT;
  },

  hasResponsiveSources(videoEl) {
    return Boolean(videoEl?.dataset?.urlMobile || videoEl?.dataset?.urlDesktop);
  },

  getResponsiveVideoUrl(videoEl) {
    if (!this.hasResponsiveSources(videoEl)) return null;

    const mobileUrl = videoEl.dataset.urlMobile || "";
    const desktopUrl = videoEl.dataset.urlDesktop || "";

    if (this.isMobileViewport()) {
      return mobileUrl || desktopUrl || null;
    }

    return desktopUrl || mobileUrl || null;
  },

  getSourceMimeType(srcUrl) {
    if (!srcUrl) return "video/mp4";

    const normalized = srcUrl.split("?")[0].split("#")[0].toLowerCase();
    if (normalized.endsWith(".m3u8")) return "application/x-mpegURL";
    if (normalized.endsWith(".webm")) return "video/webm";
    return "video/mp4";
  },

  hasPlayableSource(videoEl) {
    if (!videoEl) return false;
    if (videoEl.currentSrc) return true;

    const source = videoEl.querySelector("source");
    return Boolean(source?.getAttribute("src") || source?.src);
  },

  isVideoVisible(videoEl) {
    if (!videoEl) return false;

    const rect = videoEl.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
  },

  safePlay(videoEl) {
    if (!videoEl) return;

    const attemptPlay = () => {
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    };

    if (!this.hasPlayableSource(videoEl) && this.hasResponsiveSources(videoEl)) {
      this.syncResponsiveVideo(videoEl);
    }

    if (this.hasPlayableSource(videoEl) && videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
      return;
    }

    const onReady = () => {
      videoEl.removeEventListener("loadeddata", onReady);
      videoEl.removeEventListener("canplay", onReady);
      attemptPlay();
    };

    videoEl.addEventListener("loadeddata", onReady, { once: true });
    videoEl.addEventListener("canplay", onReady, { once: true });
  },

  playIfBackground(videoEl) {
    if (!videoEl || !this.isBackground(videoEl)) return;
    this.safePlay(videoEl);
  },

  updateVideoPoster(videoEl) {
    const mobilePoster = videoEl.dataset.posterMobile;
    const desktopPoster = videoEl.dataset.posterDesktop;
    if (!mobilePoster && !desktopPoster) return;

    const posterUrl = this.isMobileViewport()
      ? mobilePoster || desktopPoster
      : desktopPoster || mobilePoster;

    if (posterUrl && videoEl.getAttribute("poster") !== posterUrl) {
      videoEl.setAttribute("poster", posterUrl);
    }
  },

  syncResponsiveVideo(videoEl) {
    if (!videoEl || !this.hasResponsiveSources(videoEl)) return false;

    const srcUrl = this.getResponsiveVideoUrl(videoEl);
    if (!srcUrl) return false;

    let source = videoEl.querySelector("source");
    const mimeType = this.getSourceMimeType(srcUrl);
    const currentSrc = source?.getAttribute("src") || "";

    if (source) {
      if (currentSrc === srcUrl) return false;

      source.type = mimeType;
      source.src = srcUrl;
      videoEl.load();
      return true;
    }

    source = document.createElement("source");
    source.type = mimeType;
    source.src = srcUrl;
    videoEl.append(source);
    videoEl.load();
    return true;
  },

  syncSlideshowVideo(videoEl, shouldPlay) {
    if (!videoEl) return;

    const wrapper = videoEl.closest("[data-video-behavior]");
    if (!wrapper) return;

    if (shouldPlay) {
      if (this.isBackground(videoEl)) {
        this.playIfBackground(videoEl);
        return;
      }

      if (wrapper.classList.contains("theme-video--slideshow-autoplay")) {
        wrapper.classList.add("theme-video--started");
        this.safePlay(videoEl);
        this.updateSlideshowHeader();
      }
    } else {
      videoEl.pause();
      if (wrapper.classList.contains("theme-video--slideshow-autoplay")) {
        this.updateSlideshowHeader();
      }
    }
  },

  pause(videoEl) {
    if (videoEl) {
      videoEl.pause();
    }
  },

  updateSlideshowHeader() {
    const isPlaying = Boolean(
      document.querySelector(".theme-video--slideshow.theme-video--is-playing video")
    );
    document.documentElement.classList.toggle("slideshow-video-header-hidden", isPlaying);
  },

  syncVideoForViewport(videoEl, intersectionObs) {
    this.updateVideoPoster(videoEl);

    const sourceChanged = this.syncResponsiveVideo(videoEl);
    if (!sourceChanged || !this.isBackground(videoEl)) return;

    const retryIfVisible = () => {
      if (this.isVideoVisible(videoEl)) {
        this.playIfBackground(videoEl);
      }
    };

    videoEl.addEventListener("loadeddata", retryIfVisible, { once: true });

    if (intersectionObs) {
      intersectionObs.unobserve(videoEl);
      intersectionObs.observe(videoEl);
    }
  },

  initDynamicVideos(root = document) {
    const intersectionObs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const videoEl = entry.target;
        if (entry.isIntersecting) {
          window.ThemeVideo.playIfBackground(videoEl);
        } else {
          window.ThemeVideo.pause(videoEl);
        }
      });
    });

    const resizeObs = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.ThemeVideo.syncVideoForViewport(entry.target, intersectionObs);
      });
    });

    root.querySelectorAll(".js-dynamic-video").forEach((video) => {
      if (video.dataset.themeVideoInit === "true") return;
      video.dataset.themeVideoInit = "true";

      window.ThemeVideo.syncVideoForViewport(video, intersectionObs);
      resizeObs.observe(video);

      if (window.ThemeVideo.isBackground(video)) {
        intersectionObs.observe(video);
      }
    });
  },

  initPlayerControls(root = document) {
    root.querySelectorAll('[data-video-behavior="player"]').forEach((wrapper) => {
      if (wrapper.dataset.playerInit === "true") return;
      wrapper.dataset.playerInit = "true";

      const shell = wrapper.querySelector(".theme-video__shell");
      const video = wrapper.querySelector("video");
      const playBtn = wrapper.querySelector(".theme-video__button--play");
      const muteBtn = wrapper.querySelector(".theme-video__button--mute");
      const posterBtn = wrapper.querySelector(".theme-video__poster");
      const progressTrack = wrapper.querySelector(".theme-video__progress-track");
      const progressFill = wrapper.querySelector(".theme-video__progress-fill");
      if (!video || !playBtn || !muteBtn || !shell) return;

      const isSlideshow = wrapper.classList.contains("theme-video--slideshow");
      let idleTimer = null;

      const pauseOtherPlayers = () => {
        document.querySelectorAll('[data-video-behavior="player"] video').forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) {
            otherVideo.pause();
            const otherWrapper = otherVideo.closest('[data-video-behavior="player"]');
            otherWrapper?.classList.remove("theme-video--controls-hidden");
          }
        });
      };

      const updatePlayState = () => {
        wrapper.classList.toggle("theme-video--is-playing", !video.paused);
        playBtn.setAttribute("aria-label", video.paused ? "Play video" : "Pause video");

        if (video.paused) {
          wrapper.classList.remove("theme-video--controls-hidden");
          clearTimeout(idleTimer);
        } else {
          resetIdleTimer();
        }

        if (isSlideshow) {
          window.ThemeVideo.updateSlideshowHeader();
        }
      };

      const updateMuteState = () => {
        wrapper.classList.toggle("theme-video--is-muted", video.muted);
        muteBtn.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
      };

      const updateProgress = () => {
        if (!progressFill || !video.duration || Number.isNaN(video.duration)) return;
        const percent = (video.currentTime / video.duration) * 100;
        progressFill.style.width = `${Math.min(Math.max(percent, 0), 100)}%`;
      };

      const resetIdleTimer = () => {
        clearTimeout(idleTimer);
        wrapper.classList.remove("theme-video--controls-hidden");

        if (video.paused) return;

        idleTimer = setTimeout(() => {
          wrapper.classList.add("theme-video--controls-hidden");
        }, 2000);
      };

      const resetToStart = () => {
        video.pause();
        video.currentTime = 0;
        wrapper.classList.remove("theme-video--is-playing");
        wrapper.classList.remove("theme-video--controls-hidden");
        clearTimeout(idleTimer);

        if (wrapper.classList.contains("theme-video--slideshow-autoplay")) {
          wrapper.classList.add("theme-video--started");
          window.ThemeVideo.safePlay(video);
        } else {
          wrapper.classList.remove("theme-video--started");
        }

        updatePlayState();
        updateProgress();
      };

      const startPlayback = () => {
        pauseOtherPlayers();
        wrapper.classList.add("theme-video--started");
        window.ThemeVideo.safePlay(video);
      };

      const togglePlayPause = () => {
        if (video.paused) {
          startPlayback();
        } else {
          video.pause();
        }
      };

      playBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePlayPause();
      });

      muteBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        video.muted = !video.muted;
        updateMuteState();
        resetIdleTimer();
      });

      posterBtn?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        startPlayback();
      });

      progressTrack?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!video.duration || Number.isNaN(video.duration)) return;

        const rect = progressTrack.getBoundingClientRect();
        const percent = (event.clientX - rect.left) / rect.width;
        video.currentTime = Math.min(Math.max(percent, 0), 1) * video.duration;
        updateProgress();
        resetIdleTimer();
      });

      shell.addEventListener("click", (event) => {
        if (event.target.closest(".theme-video__button, .theme-video__progress-track, .theme-video__poster")) {
          return;
        }

        if (isSlideshow && !wrapper.classList.contains("theme-video--started")) {
          startPlayback();
          return;
        }

        togglePlayPause();
      });

      wrapper.addEventListener("mousemove", resetIdleTimer);
      wrapper.addEventListener("touchstart", resetIdleTimer, { passive: true });

      video.addEventListener("play", updatePlayState);
      video.addEventListener("pause", updatePlayState);
      video.addEventListener("volumechange", updateMuteState);
      video.addEventListener("timeupdate", updateProgress);
      video.addEventListener("loadedmetadata", updateProgress);
      video.addEventListener("ended", resetToStart);

      if (!isSlideshow) {
        wrapper.classList.add("theme-video--started");
      }

      updatePlayState();
      updateMuteState();
      updateProgress();
    });
  },
};

document.addEventListener("DOMContentLoaded", () => {
  window.ThemeVideo.initDynamicVideos();
  window.ThemeVideo.initPlayerControls();
});

document.addEventListener("shopify:section:load", (event) => {
  window.ThemeVideo.initDynamicVideos(event.target);
  window.ThemeVideo.initPlayerControls(event.target);
});
